import { createFormHook, createFormHookContexts } from '@tanstack/react-form';
import { FormField } from '../fields/form-field';

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

export const { useAppForm, withFieldGroup } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    FormField,
  },
  formComponents: {},
});

interface UseDynamicFormArgs {
  sectionId: string;
  defaultValues: Record<string, unknown>;
  onNext: (values: Record<string, unknown>) => void;
  onChangeListener?: (values: Record<string, unknown>) => void;
}

const FOCUSABLE_SELECTOR = [
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// a FormField wrapper (data-invalid) isn't focusable itself, so focus the invalid control inside it, else its first control
const getFocusTarget = (invalidElement: HTMLElement) => {
  if (!invalidElement.hasAttribute('data-invalid')) return invalidElement;

  const controls = Array.from(invalidElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  return controls.find((control) => control.getAttribute('aria-invalid') === 'true') ?? controls[0];
};

// waits for the post-validation re-render (aria-invalid/data-invalid updates) before searching the DOM
const scrollToFirstInvalidField = (formId: string) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const invalidElement = document
        .getElementById(formId)
        ?.querySelector<HTMLElement>('[data-invalid="true"], [aria-invalid="true"]');
      if (!invalidElement) return;

      const focusTarget = getFocusTarget(invalidElement);
      (focusTarget ?? invalidElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
      focusTarget?.focus({ preventScroll: true });
    });
  });
};

export const useDynamicForm = ({ sectionId, defaultValues, onNext, onChangeListener }: UseDynamicFormArgs) => {
  return useAppForm({
    formId: `dynamic-form-${sectionId}`,
    defaultValues,
    onSubmit: ({ value }) => onNext(value),
    onSubmitInvalid: () => scrollToFirstInvalidField(`dynamic-form-${sectionId}`),
    listeners: {
      onChangeDebounceMs: 500,
      onChange: ({ formApi }) => onChangeListener?.(formApi.store.get().values),
    },
  });
};

export type DynamicFormApi = ReturnType<typeof useDynamicForm>;
